<template>
  <div class="sources-list sources-list--open">
    <div class="sources-list__overlay" @click="handleClose('overlay')"></div>
    <div class="sources-list__panel">
      <div class="sources-list__header">
        <Button
          variant="ghost"
          size="icon"
          class="sources-list__back"
          @click="handleClose('back_button')"
          aria-label="Close sources"
        >
          <ChevronRight :size="24" />
        </Button>
        <h2 class="sources-list__title">Sources</h2>
      </div>

      <div class="sources-list__content">
        <ScrollArea v-if="sources.length > 0" class="sources-list__scroll">
          <div class="sources-list__items">
            <div
              v-for="(source, index) in parsedSources"
              :key="index"
              class="sources-list__item"
              :class="{ 'sources-list__item--clickable': source.url }"
              @click="source.url && openUrl(source.url, index)"
            >
              <div class="sources-list__icon">
                <Globe v-if="source.url" :size="20" />
                <FileText v-else :size="20" />
              </div>
              <div class="sources-list__content">
                <span class="sources-list__text">
                  {{ source.parsedName }}
                  <Badge
                    v-if="source.extension"
                    variant="secondary"
                    class="sources-list__badge"
                  >
                    {{ source.extension }}
                  </Badge>
                </span>
              </div>
              <ExternalLink
                v-if="source.url"
                :size="20"
                class="sources-list__external"
              />
            </div>
          </div>
        </ScrollArea>

        <div v-else class="sources-list__empty">
          <FileText :size="48" class="sources-list__empty-icon" />
          <p class="sources-list__empty-text">No sources available</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { Button } from '@/lib/ui/button'
import { Badge } from '@/lib/ui/badge'
import { ScrollArea } from '@/lib/ui/scroll-area'
import { ChevronRight, FileText, Globe, ExternalLink } from 'lucide-vue-next'
import { parseSourceDocuments } from '@/utils/text'
import { useAnalytics } from '@/composables/useAnalytics'
import { useChatSession } from '@/composables/useChatSession'
import { ANALYTICS_EVENTS, ANALYTICS_SOURCE } from '@/types/analytics-types'
import type { ISourcesListProps, ISourcesListEmits } from './types'

const props = defineProps<ISourcesListProps>()
const emit = defineEmits<ISourcesListEmits>()

const analytics = useAnalytics()
const chatSession = useChatSession()

const sources = computed(() => props.sources || [])

const parsedSources = computed(() => parseSourceDocuments(sources.value))

const panelOpenedAt = ref<number>(Date.now())
const documentsClicked = ref(0)
const closeEventTracked = ref(false)

onMounted(() => {
  panelOpenedAt.value = Date.now()
  documentsClicked.value = 0
  closeEventTracked.value = false
})

onUnmounted(() => {
  if (closeEventTracked.value) return

  const timeOpen = Date.now() - panelOpenedAt.value

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SOURCE_PANEL_CLOSED,
    timestamp: new Date().toISOString(),
    payload: {
      timeOpen: timeOpen,
      documentsClicked: documentsClicked.value,
      totalDocuments: sources.value.length,
      closeMethod: 'unmount',
      sessionId: chatSession.store.activeSessionId || '',
    },
    source: ANALYTICS_SOURCE,
    sessionId: chatSession.store.activeSessionId || undefined,
  })
})

const openUrl = (url: string, index: number): void => {
  documentsClicked.value++

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SOURCE_DOCUMENT_CLICKED,
    timestamp: new Date().toISOString(),
    payload: {
      documentName: sources.value[index]?.name || '',
      documentUrl: url,
      documentIndex: index,
      totalDocuments: sources.value.length,
      location: 'panel',
      sessionId: chatSession.store.activeSessionId || '',
    },
    source: ANALYTICS_SOURCE,
    sessionId: chatSession.store.activeSessionId || undefined,
  })

  window.open(url, '_blank', 'noopener,noreferrer')
}

const handleClose = (
  method: 'back_button' | 'overlay' = 'back_button'
): void => {
  const timeOpen = Date.now() - panelOpenedAt.value

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SOURCE_PANEL_CLOSED,
    timestamp: new Date().toISOString(),
    payload: {
      timeOpen: timeOpen,
      documentsClicked: documentsClicked.value,
      totalDocuments: sources.value.length,
      closeMethod: method,
      sessionId: chatSession.store.activeSessionId || '',
    },
    source: ANALYTICS_SOURCE,
    sessionId: chatSession.store.activeSessionId || undefined,
  })

  closeEventTracked.value = true
  emit('update:isOpen', false)
}
</script>

<style>
.sources-list {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: calc(var(--widget-z-index, 9997) + 10);
  pointer-events: auto;
}

.sources-list__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 1;
  z-index: 1;
}

.sources-list__panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  background: hsl(var(--background));
  box-shadow:
    -4px 0 20px rgba(0, 0, 0, 0.15),
    -2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transform: translateX(0);
  z-index: 2;
}

.sources-list__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid hsl(var(--border));
  flex-shrink: 0;
}

.sources-list__back {
  flex-shrink: 0;
  color: hsl(var(--foreground));
}

.sources-list__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: hsl(var(--foreground));
  margin: 0;
  line-height: 1.2;
}

.sources-list__content {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.sources-list__scroll {
  height: 100%;
  padding: 16px;
}

.sources-list__items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sources-list__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: default;
  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
}

.sources-list__item--clickable {
  cursor: pointer;
}

.sources-list__item--clickable:hover {
  background-color: hsl(var(--primary) / 0.05);
  border-color: hsl(var(--primary) / 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sources-list__icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: hsl(var(--muted));
  border-radius: 8px;
  color: hsl(var(--muted-foreground));
}

.sources-list__icon svg[data-lucide='globe'] {
  color: hsl(var(--primary));
}

.sources-list__content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.sources-list__text {
  font-size: 0.9375rem;
  font-weight: 500;
  color: hsl(var(--foreground));
  line-height: 1.4;
  word-break: break-word;
  display: inline-block;
}

.sources-list__badge {
  display: inline-flex;
  vertical-align: middle;
  margin-left: 6px;
}

.sources-list__external {
  flex-shrink: 0;
  color: hsl(var(--muted-foreground));
  transition: color 0.2s ease;
}

.sources-list__item--clickable:hover .sources-list__external {
  color: hsl(var(--primary));
}

.sources-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
  width: 100%;
  height: 100%;
}

.sources-list__empty-icon {
  color: hsl(var(--muted-foreground));
  opacity: 0.5;
}

.sources-list__empty-text {
  font-size: 0.875rem;
  color: hsl(var(--muted-foreground));
  margin: 0;
  text-align: center;
}
</style>
